import { useFetcher } from "@remix-run/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { CustomField, Pagination, WovarProduct } from "@commercekit/api";
import { useDebounce, useErrorAlert, useLocationSearch } from "@commercekit/react";
import { useLocalePath } from "./useLocale";

export const useLoadMoreWovarProducts = (
  products: WovarProduct[],
  categoryId: string,
  filters: CustomField[],
  pagination: Pagination | undefined,
  // WOV-2195: off in the numbered-pagination variant, so the scroll listener
  // can't keep appending products underneath the page links.
  enabled = true
) => {
  const { search } = useLocationSearch();
  const [exhibitProduct, setExhibitProduct] = useState<WovarProduct[]>(products || []);
  const [exhibitFilter, setExhibitFilter] = useState<CustomField[]>(filters || []);
  useEffect(() => {
    setExhibitProduct(products);
  }, [products]);
  useEffect(() => {
    setExhibitFilter(filters);
  }, [filters]);
  const loadRef = useRef<HTMLDivElement>(null);

  const loadFetcher = useFetcher<{
    products: WovarProduct[];
    pagination: Pagination | undefined;
    filters: CustomField[];
    errors: string[];
  }>();

  const loadMoreLoading = useMemo(() => {
    return loadFetcher.state !== "idle";
  }, [loadFetcher.state]);

  const [hasNextPage, setHasNextPage] = useState(pagination?.hasNextPage || false);
  const [endCursor, setEndCursor] = useState(pagination?.endCursor || "");
  const localePath = useLocalePath();
  const submit = useCallback(() => {
    loadFetcher.submit(
      {
        after: endCursor,
        categoryId: categoryId,
        filter: search.get("filter") || "",
        // WOV-2195: carry the active sort, otherwise each batch returns in default rank order.
        sort_name: search.get("sort_name") || "",
        sort_direction: search.get("sort_direction") || "",
      },
      { method: "get", action: localePath("/loadmore") }
    );
  }, [categoryId, endCursor, loadFetcher, localePath, search]);
  const loadMore = useCallback(() => {
    if (typeof window === "undefined") return;
    if (loadMoreLoading || !hasNextPage) return;
    const top = loadRef.current?.getBoundingClientRect().top;
    const triggerHeight =
      (window.visualViewport?.height ?? window.innerHeight) * 1.5;

    if (top != null && top < triggerHeight) {
      submit();
    }
  }, [hasNextPage, loadMoreLoading, submit]);
  const onScroll = useDebounce(loadMore, 30);
  const { show } = useErrorAlert();
  useEffect(() => {
    if (!loadFetcher.data) return;
    if (loadFetcher.data?.errors && loadFetcher.data.errors.length > 0) {
      show(loadFetcher.data.errors.join(","), "warning");
      return;
    }
    setExhibitProduct((list) => [...list, ...(loadFetcher.data?.products || [])]);
    setExhibitFilter((list) => [...list, ...(loadFetcher.data?.filters || [])]);
    setHasNextPage(!!loadFetcher.data?.pagination?.hasNextPage);
    setEndCursor(loadFetcher.data?.pagination?.endCursor || "");
  }, [loadFetcher.data, show]);
  useEffect(() => {
    setHasNextPage(pagination?.hasNextPage || false);
    setEndCursor(pagination?.endCursor || "");
  }, [pagination]);
  useEffect(() => {
    if (typeof window === "undefined" || !enabled) return;
    // Body or window may be the scroller depending on html/body overflow CSS; listen on both.
    document.body.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      document.body.removeEventListener("scroll", onScroll);
      window.removeEventListener("scroll", onScroll);
    };
  }, [enabled, onScroll]);

  return {
    exhibitProduct,
    exhibitFilter,
    loadMoreLoading,
    loadRef,
    loadMore,
    hasNextPage,
    endCursor,
    loadFetcher,
  };
};
